import { useTranslation } from 'react-i18next';
import coverImage from '@/assets/icons/in-app-browser-notice-cover.png';
import CloseIcon from '@/assets/icons/in-app-close.svg?react';
import { Button, Modal, Text } from '@/components/base';
import { ManualOpenSteps } from '@/components/InAppBrowserNotice/components/ManualOpenSteps';

export interface InAppBrowserNoticeProps {
  open: boolean;
  link: string;
  canOpenExternal: boolean;
  isLinkCopied: boolean;
  onOpenExternal: () => void;
  onCopyLink: () => void;
  onClose: () => void;
}

/**
 * Soft warning in an in-app browser: speaking works better in the device's
 * default browser, but the learner can always close it and stay.
 */
export const InAppBrowserNotice = ({
  open,
  link,
  canOpenExternal,
  isLinkCopied,
  onOpenExternal,
  onCopyLink,
  onClose,
}: InAppBrowserNoticeProps) => {
  const { t } = useTranslation('activitySession');

  return (
    <Modal
      open={open}
      onClose={onClose}
      // 24px gutter on narrow viewports, matching the design, instead of the default 16.
      containerClassnames="p-6"
      // `rounded-[20px]`, not a token — tailwind-merge would keep Modal's `rounded-2xl` too.
      className="max-w-[345px] rounded-[20px] px-6 pb-6 pt-0 font-fustat"
    >
      <div className="flex flex-col items-center gap-4">
        <img src={coverImage} alt="" className="-mx-6 aspect-[345/181] w-[calc(100%+48px)] max-w-none" />
        <div className="flex w-full flex-col items-center gap-2 text-center">
          <Text version="revamp-v1" variant="body3" className="font-semibold leading-7 text-revamp-gray-900">
            {t('in_app_browser_notice.title')}
          </Text>
          <Text version="revamp-v1" variant="body5" className="font-medium leading-5 text-revamp-gray-500">
            {t('in_app_browser_notice.description')}
          </Text>
        </div>
        {canOpenExternal ? (
          <Button
            version="revamp-v1"
            variant="solid"
            color="primary"
            size="xl"
            className="w-full leading-7"
            onClick={onOpenExternal}
          >
            {t('in_app_browser_notice.open_external')}
          </Button>
        ) : (
          <ManualOpenSteps link={link} isLinkCopied={isLinkCopied} onCopyLink={onCopyLink} />
        )}
      </div>
      <Button
        variant="unstyled"
        onClick={onClose}
        aria-label={t('in_app_browser_notice.close')}
        className="absolute right-6 top-6 flex size-6 items-center justify-center rounded-full bg-revamp-gray-200"
      >
        <CloseIcon aria-hidden className="size-[18px]" />
      </Button>
    </Modal>
  );
};
